import React, { useState } from "react";
import { api, apiError } from "@/lib/api";
import { siglaZona } from "@/lib/format";
import { activas, misEtapas, normContenedor, fechaHoraEn, paredEn, docDeEtapa, CONFIRMAR_EN } from "@/lib/chofer";
import { InfoEtapa } from "@/components/chofer/InfoEtapa";
import { AgregarArchivo, ListaArchivos } from "@/components/chofer/AgregarArchivo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, TriangleAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/lib/i18n";
// Fix Q (2026-10-13): la etapa en curso del chofer. Cada acción llama a su ruta (llegada, registro, ETA, salida) y el servidor
// devuelve la carga actualizada; las horas las pone el servidor. Lo que el chofer escribe se guarda al salir del campo y
// otra vez al confirmar, así dispatch lo ve enseguida y navegar entre pasos no borra nada.
const CHECK = {
  delivery: { campo: "descarga_terminada", get texto() { return t("Unloading finished; container is empty"); } },
  return_empty: { campo: "vacio_devuelto", get texto() { return t("Empty container returned"); } },
  return_chassis: { campo: "chassis_devuelto", get texto() { return t("Chassis returned"); } },
};
function Etiqueta({ children }) {
  return <p className="mt-4 mb-1 text-[13.5px] text-ink-2">{children}</p>;
}
export function EtapaActual({ carga, e, numero, uid, form, setForm, onCarga, onSalio, onEta, onFiles, onRecap, nombreChofer }) {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [casilla, setCasilla] = useState(null); // lo que el chofer acaba de tocar, hasta que el servidor responde
  const total = misEtapas(carga, uid).length;
  const esUltima = numero === total;
  const ultimaDeLaCarga = activas(carga).slice(-1)[0]?.clave === e.clave;
  const llegada = !!e.llegada_at;
  const entrega = activas(carga).find((x) => x.clave === "delivery");
  const zonaReceiver = entrega?.zona_horaria || "America/Chicago";
  const vigente = carga.eta_vigente;
  const doc = docDeEtapa(carga, e.clave);
  const archivosEtapa = (carga.archivos || []).filter((a) => a.etapa === e.clave);
  const misArchivosN = (carga.archivos || []).filter((a) => a.origen === "chofer" && a.chofer_id === uid).length;
  const check = CHECK[e.clave];
  const esRail = e.clave === "rail_pickup";
  const esVacio = e.clave === "empty_pickup";
  const pideContenedor = esRail || esVacio;
  const pideChassis = e.clave === "pick_chassis" || esRail;
  const coincide = !esRail || !form.contenedor || normContenedor(form.contenedor) === carga.numero_contenedor;
  const ruta = (r) => `/chofer/cargas/${carga.id}/etapas/${e.clave}/${r}`;
  const llamar = async (nombre, fn) => {
    setBusy(nombre);
    try { return await fn(); } catch (err) { toast.error(apiError(err)); return null; } finally { setBusy(""); }
  };
  const registrarLlegada = () => llamar("llegada", async () => {
    setError("");
    const { data } = await api.post(ruta("llegada"));
    onCarga(data);
    toast.success(t("Arrival recorded"));
  });
  // Solo manda lo que cambió respecto de lo que ya tiene el servidor.
  const cambios = () => {
    const d = {};
    if (pideChassis) { const v = (form.chassis || "").trim().toUpperCase(); if (v !== (e.chassis_reportado || "")) d.chassis = v; }
    if (pideContenedor) { const v = normContenedor(form.contenedor); if (v !== (e.contenedor_reportado || "")) d.contenedor = v; }
    if (e.clave === "yard_stop") { const v = (form.espacio || "").trim(); if (v !== (e.espacio || "")) d.espacio = v; }
    return d;
  };
  const guardarRegistro = async () => {
    const d = cambios();
    if (!llegada || Object.keys(d).length === 0) return;
    const { data } = await api.post(ruta("registro"), d);
    onCarga(data);
  };
  const alSalirDelCampo = () => { guardarRegistro().catch((err) => toast.error(apiError(err))); };
  // La casilla cambia al instante; si el servidor la rechaza vuelve a como estaba.
  const marcar = async (campo, valor) => {
    setCasilla(valor);
    try {
      const { data } = await api.post(ruta("registro"), { [campo]: valor });
      onCarga(data);
    } catch (err) { toast.error(apiError(err)); } finally { setCasilla(null); }
  };
  const problemas = () => {
    const p = [];
    if (pideChassis && !(form.chassis || "").trim()) p.push(t("Enter the reported chassis."));
    if (esRail) {
      if (!normContenedor(form.contenedor)) p.push(t("Enter the reported container."));
      else if (!coincide) p.push(t("The reported container does not match the assigned container. Correct it to continue."));
      if (!form.etaFecha || !form.etaHora) p.push(t("Enter the Delivery ETA before departure."));
    }
    if (esVacio && !normContenedor(form.contenedor)) p.push(t("Enter the container number you picked up."));
    if (e.clave === "yard_stop" && !(form.espacio || "").trim()) p.push(t("Enter the yard spot."));
    if (check && !e[check.campo]) p.push(t("Confirm: {texto}.", { texto: check.texto.toLowerCase() }));
    return p;
  };
  const confirmar = async () => {
    setError("");
    if (!llegada) { setError(t("Record your arrival first.")); return; }
    const p = problemas();
    if (p.length) { setError(p.join(" ")); return; }
    setBusy("salida");
    try {
      await guardarRegistro();
      if (esRail && (!vigente || vigente.eta_fecha !== form.etaFecha || vigente.eta_hora !== form.etaHora)) {
        const { data } = await api.post(`/chofer/cargas/${carga.id}/eta`, { eta_fecha: form.etaFecha, eta_hora: form.etaHora });
        onCarga(data);
      }
      const { data } = await api.post(ruta("salida"));
      onCarga(data);
      toast.success(ultimaDeLaCarga ? t("Trip finished") : t("Departure recorded"));
      onSalio();
    } catch (err) { setError(apiError(err)); } finally { setBusy(""); }
  };
  return (
    <section className="bg-white border border-line rounded-[14px] p-4" data-testid={`etapa-actual-${e.clave}`}>
      <InfoEtapa carga={carga} uid={uid} e={e} eyebrow={t("Current · Step {numero} of {total}", { numero, total })} mostrarProxima={esRail} />
      <Button type="button" variant="outline" data-testid="registrar-llegada-btn" disabled={llegada || !!busy} onClick={registrarLlegada} className="mt-4 w-full h-[52px] text-[16px] font-bold text-navy-2">
        {busy === "llegada" ? <Loader2 className="h-5 w-5 animate-spin" /> : t("Record arrival")}
      </Button>
      <p className="mt-2 text-[15.5px] text-ink" data-testid="llegada-texto">{t("Arrival: ")}{llegada ? fechaHoraEn(e.llegada_at, e.zona_horaria) : t("Pending")}</p>
      {pideContenedor && (
        <>
          <Etiqueta>{t("Reported container · required")}</Etiqueta>
          <Input
            data-testid="campo-contenedor" value={form.contenedor} disabled={!llegada} onBlur={alSalirDelCampo}
            onChange={(ev) => setForm({ contenedor: ev.target.value.toUpperCase() })}
            autoCapitalize="characters" autoCorrect="off" autoComplete="off" spellCheck={false}
            className={`h-[52px] text-[17px] font-mono uppercase ${coincide ? "" : "border-danger border-2"}`}
          />
          {esRail && form.contenedor && coincide && <p className="mt-1.5 text-[14px] font-semibold text-ok flex items-center gap-1" data-testid="contenedor-coincide"><CheckCircle2 className="h-4 w-4" />{t(" Matches assigned container")}</p>}
          {!coincide && (
            <div className="mt-2 rounded-lg border-2 border-danger bg-[#FBEDEC] p-3" data-testid="contenedor-no-coincide">
              <p className="flex items-center gap-2 font-bold text-danger"><TriangleAlert className="h-5 w-5 shrink-0" />{t(" Container does not match")}</p>
              <p className="text-[14px] text-danger mt-1">{t("Assigned container: ")}<span className="font-mono font-semibold">{carga.numero_contenedor}</span>{t(". Correct the reported container to confirm the pickup.")}</p>
            </div>
          )}
        </>
      )}
      {pideChassis && (
        <>
          <Etiqueta>{t("Reported chassis · required")}</Etiqueta>
          <Input
            data-testid="campo-chassis" value={form.chassis} disabled={!llegada} onBlur={alSalirDelCampo}
            onChange={(ev) => setForm({ chassis: ev.target.value.toUpperCase() })}
            autoCapitalize="characters" autoCorrect="off" autoComplete="off" spellCheck={false}
            className="h-[52px] text-[17px] font-mono uppercase"
          />
        </>
      )}
      {e.clave === "yard_stop" && (
        <>
          <Etiqueta>{t("Yard spot · required")}</Etiqueta>
          <Input data-testid="campo-espacio" value={form.espacio} disabled={!llegada} onBlur={alSalirDelCampo} onChange={(ev) => setForm({ espacio: ev.target.value })} placeholder={t("Where did you leave the equipment?")} maxLength={40} className="h-[52px] text-[17px]" />
        </>
      )}
      {esRail && (
        <>
          <Etiqueta>{t("Delivery ETA · required before departure")}</Etiqueta>
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="campo-eta-fecha" type="date" value={form.etaFecha} disabled={!llegada} onChange={(ev) => setForm({ etaFecha: ev.target.value })} className="h-[52px] text-[16px]" />
            <Input data-testid="campo-eta-hora" type="time" value={form.etaHora} disabled={!llegada} onChange={(ev) => setForm({ etaHora: ev.target.value })} className="h-[52px] text-[16px]" />
          </div>
          <p className="mt-1.5 text-[13px] text-ink-2">{t("Receiver local time · {zonaReceiver} ({v1})", { zonaReceiver, v1: siglaZona(entrega?.zona_horaria) })}</p>
        </>
      )}
      {(e.clave === "delivery" || e.clave === "yard_stop") && (
        <>
          <Etiqueta>{t("Delivery ETA")}</Etiqueta>
          <div className="rounded-md border border-line-strong bg-white px-3 min-h-[52px] flex items-center text-[17px] text-ink" data-testid="eta-vigente">
            {vigente ? paredEn(vigente.eta_fecha, vigente.eta_hora, vigente.zona_horaria) : t("Not set")}
          </div>
          <Button type="button" variant="outline" data-testid="update-eta-btn" onClick={onEta} className="mt-2 w-full h-[52px] text-[16px] font-bold text-navy-2">{t("Update ETA")}</Button>
        </>
      )}
      {e.clave === "return_chassis" && carga.chasis_numero && <p className="mt-4 font-bold text-[16px] text-navy-2" data-testid="chassis-reportado">{t("Chassis: {chasis_numero}", { chasis_numero: carga.chasis_numero })}</p>}
      {check && (
        <label className="mt-4 flex items-center gap-3 min-h-[44px] text-[16px] text-ink">
          <input type="checkbox" data-testid="check-etapa" className="h-6 w-6 accent-[#277A3F]" checked={casilla ?? !!e[check.campo]} disabled={!llegada} onChange={(ev) => marcar(check.campo, ev.target.checked)} />
          {check.texto}
        </label>
      )}
      <Etiqueta>{doc.nombre} · {doc.requerido ? t("required") : t("optional")}</Etiqueta>
      {llegada
        ? <AgregarArchivo cargaId={carga.id} etapa={e.clave} tipo={doc.tipo} escaner={e.clave === "delivery"} onCarga={onCarga} />
        : <p className="text-[13.5px] text-ink-3" data-testid="archivos-bloqueado">{t("Record your arrival to add files.")}</p>}
      <ListaArchivos archivos={archivosEtapa} zona={e.zona_horaria} />
      {esUltima ? (
        <div className="mt-5 grid grid-cols-[1fr_1.6fr] gap-2">
          <Button type="button" variant="outline" data-testid="files-btn" onClick={onFiles} className="h-[52px] text-[16px] font-bold text-navy-2">{t("Files ({misArchivosN})", { misArchivosN })}</Button>
          <Button type="button" data-testid="confirmar-btn" disabled={!!busy || !coincide} onClick={confirmar} className="h-[52px] text-[16px] font-bold">
            {busy === "salida" ? <Loader2 className="h-5 w-5 animate-spin" /> : CONFIRMAR_EN[e.clave]}
          </Button>
        </div>
      ) : (
        <Button type="button" data-testid="confirmar-btn" disabled={!!busy || !coincide} onClick={confirmar} className="mt-5 w-full h-[52px] text-[16px] font-bold">
          {busy === "salida" ? <Loader2 className="h-5 w-5 animate-spin" /> : CONFIRMAR_EN[e.clave]}
        </Button>
      )}
      {error && <p className="mt-2 text-[14px] font-semibold text-danger" role="alert" data-testid="confirmar-error">{error}</p>}
      <p className="mt-3 text-[13px] text-ink-2">{t("Required documents can be added later.")}</p>
      <button type="button" data-testid="ver-recap" onClick={onRecap} className="mt-1 text-left font-display font-bold text-[16px] text-navy-2 underline underline-offset-2">{t("My route · {nombreChofer}", { nombreChofer })}
      </button>
      <p className="text-[12.5px] text-ink-3">{t("Tap to view the trip recap")}</p>
    </section>
  );
}
